import { hexToRgb } from '@flobi/artwork-core';

/**
 * The sums behind the colour picker's lists: when two colours are the same to an eye, which
 * brand colour one is (or is nearest), and which colours a file paints with.
 *
 * "The same to an eye" is ΔE — the distance between two colours in CIELAB, where equal
 * steps look equal. Side by side, about 2.3 is the least anyone notices; in a row of small
 * dots it takes more, and 5 is used here so the near-twins a drag leaves behind (#E3000F
 * and #E3001B in Tarek's Recent, 4.7 apart) count once.
 */

/** Two colours closer than this are one colour in a row of swatches. */
export const SAME = 5;
/** A brand colour further than this from the one in hand is not offered as "the nearest". */
export const NEAR = 30;
/** A colour with less chroma than this is a white, a grey or a black. */
export const NEUTRAL = 6;

/** A hex in CIELAB (D65), the space where equal steps look equal. */
export function labOf(hex: string): [number, number, number] {
  const { r, g, b } = hexToRgb(hex);
  const linear = (c: number) => {
    const v = c / 255;
    return v <= 0.04045 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4;
  };
  const [R, G, B] = [linear(r), linear(g), linear(b)];
  const f = (t: number) => (t > 0.008856 ? Math.cbrt(t) : 7.787 * t + 16 / 116);
  const x = f((R * 0.4124 + G * 0.3576 + B * 0.1805) / 0.95047);
  const y = f(R * 0.2126 + G * 0.7152 + B * 0.0722);
  const z = f((R * 0.0193 + G * 0.1192 + B * 0.9505) / 1.08883);
  return [116 * y - 16, 500 * (x - y), 200 * (y - z)];
}

/** How different two colours look (ΔE 1976). */
export function deltaE(a: string, b: string): number {
  const [l1, a1, b1] = labOf(a);
  const [l2, a2, b2] = labOf(b);
  return Math.hypot(l1 - l2, a1 - a2, b1 - b2);
}

/** The list with each colour kept once: a later one an eye cannot tell from an earlier one is dropped. */
export function distinctColours(list: readonly string[], tolerance = SAME): string[] {
  const kept: string[] = [];
  for (const hex of list) {
    const one = hex.toLowerCase();
    if (!kept.some((earlier) => deltaE(earlier, one) < tolerance)) kept.push(one);
  }
  return kept;
}

export interface NamedColour {
  readonly name: string;
  readonly hex: string;
}

/**
 * What the colour in hand is to the brand: one of its colours (by name), near one of them
 * (the nearest, to offer instead), or neither.
 */
export type BrandStanding = { readonly kind: 'brand'; readonly name: string } | { readonly kind: 'near'; readonly name: string; readonly hex: string } | { readonly kind: 'off' };

export function brandStanding(hex: string, swatches: readonly NamedColour[]): BrandStanding | null {
  if (swatches.length === 0) return null;
  const one = hex.toLowerCase();
  const exact = swatches.find((swatch) => swatch.hex.toLowerCase() === one);
  if (exact) return { kind: 'brand', name: exact.name };
  // White, black and the greys between are every brand's: a brand rarely lists them, and
  // calling a white page "off-brand" would be the line crying wolf.
  const [, a, b] = labOf(one);
  if (Math.hypot(a, b) < NEUTRAL) return null;
  let best: { swatch: NamedColour; distance: number } | null = null;
  for (const swatch of swatches) {
    const distance = deltaE(one, swatch.hex);
    if (!best || distance < best.distance) best = { swatch, distance };
  }
  return best && best.distance < NEAR ? { kind: 'near', name: best.swatch.name, hex: best.swatch.hex.toLowerCase() } : { kind: 'off' };
}

/** A paint as the file stores it, read loosely: only what a colour count needs. */
interface PaintLike {
  readonly type?: string;
  readonly visible?: boolean;
  readonly color?: { r: number; g: number; b: number };
  readonly stops?: readonly { color?: { r: number; g: number; b: number } }[];
}

const hexOfChannels = ({ r, g, b }: { r: number; g: number; b: number }) =>
  `#${[r, g, b].map((v) => Math.round(Math.min(1, Math.max(0, v)) * 255).toString(16).padStart(2, '0')).join('')}`;

/**
 * Every colour a file paints with, the most used first: each layer's fills and strokes, a
 * text's own fills, and every stop of every gradient. Hidden paints are not counted; a
 * near-twin counts as the colour it is a twin of.
 */
export function fileColours(paintLists: Iterable<readonly PaintLike[] | undefined>, most = 24): string[] {
  const counts = new Map<string, number>();
  const count = (color: { r: number; g: number; b: number } | undefined) => {
    if (!color) return;
    const hex = hexOfChannels(color);
    counts.set(hex, (counts.get(hex) ?? 0) + 1);
  };
  for (const paints of paintLists) {
    for (const paint of paints ?? []) {
      if (paint.visible === false) continue;
      if (paint.type === 'solid') count(paint.color);
      else for (const stop of paint.stops ?? []) count(stop.color);
    }
  }
  const ranked = [...counts].sort((a, b) => b[1] - a[1]).map(([hex]) => hex);
  return distinctColours(ranked).slice(0, most);
}

/** A brand gradient as a linear paint across the box: CSS's angle (0 is up, 90 to the right) as the line it runs along. */
export function gradientLine(angle: number): { start: { x: number; y: number }; end: { x: number; y: number } } {
  const radians = (angle * Math.PI) / 180;
  const run = { x: Math.sin(radians) / 2, y: -Math.cos(radians) / 2 };
  return { start: { x: 0.5 - run.x, y: 0.5 - run.y }, end: { x: 0.5 + run.x, y: 0.5 + run.y } };
}
